import React, { useState } from "react";
import { Autocomplete, Box, Button, Checkbox, Chip, FormControlLabel, MenuItem, Popover, Select, TextField, Typography } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

import { useGetCostCenterUsersQuery } from "state/rtkQuery/user/userQueries";

import {
  ACTION_VALUE_OPTIONS,
  FILTER_FIELDS,
  STATUS_VALUE_OPTIONS,
  activeFilterKeys,
  canFilterByReviewer,
  clearAllFilters,
  getFilterChips,
  setFilterValue,
  toggleFilter,
} from "./ulbFilters";

const ACCENT = "#26890d";
const inputSx = { backgroundColor: "#fff", "& .MuiInputBase-root": { fontSize: "0.85rem" } };

const useCostCenters = () => {
  const { data } = useGetCostCenterUsersQuery();
  const costCenters = data?.cost_centers ?? [];
  return { costCenters, costCenterNameById: Object.fromEntries(costCenters.map((cc) => [cc._id, cc.name])) };
};

// "Filters" button + panel. One labelled row per filter: tick it to switch it on
// and its input appears underneath. All ticked filters apply together and take
// effect as you change them. What is applied shows as chips (<UlbFilterChips>).
export const UlbRequestFilters = ({ filters, onFiltersChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { costCenters } = useCostCenters();
  const count = activeFilterKeys(filters).length;
  const setValue = (key) => (value) => onFiltersChange(setFilterValue(filters, key, value));

  const valueSelect = (key, options, placeholder) => (
    <Select
      size="small"
      fullWidth
      displayEmpty
      value={filters[key].value || ""}
      onChange={(e) => setValue(key)(e.target.value)}
      renderValue={(value) => options.find((o) => o.value === value)?.label ?? <span style={{ color: "#999" }}>{placeholder}</span>}
      inputProps={{ "aria-label": FILTER_FIELDS.find((f) => f.key === key).label }}
      sx={{ ...inputSx, fontSize: "0.85rem" }}
    >
      {options.map((opt) => (
        <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: "0.85rem" }}>
          {opt.label}
        </MenuItem>
      ))}
    </Select>
  );

  const controls = {
    status: () => valueSelect("status", STATUS_VALUE_OPTIONS, "Select a status"),
    action: () => valueSelect("action", ACTION_VALUE_OPTIONS, "Select a type"),
    request_date: () => (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box sx={{ display: "flex", gap: 1 }}>
          {["from", "to"].map((end) => (
            <DatePicker
              key={end}
              disableFuture
              label={end === "from" ? "From" : "To"}
              minDate={end === "to" ? filters.request_date.value.from || undefined : undefined}
              value={filters.request_date.value[end] || null}
              onChange={(newValue) => setValue("request_date")({ ...filters.request_date.value, [end]: newValue })}
              renderInput={(params) => (
                <TextField {...params} size="small" sx={{ ...inputSx, flex: 1, minWidth: 0 }} inputProps={{ ...params.inputProps, placeholder: end === "from" ? "From date" : "To date" }} />
              )}
            />
          ))}
        </Box>
      </LocalizationProvider>
    ),
    cost_center: () => (
      <Autocomplete
        multiple
        size="small"
        limitTags={2}
        options={costCenters}
        getOptionLabel={(option) => option?.name ?? ""}
        isOptionEqualToValue={(option, value) => option._id === value._id}
        value={costCenters.filter((cc) => filters.cost_center.value.includes(cc._id))}
        onChange={(_, options) => setValue("cost_center")(options.map((option) => option._id))}
        renderInput={(params) => (
          <TextField {...params} placeholder={filters.cost_center.value.length ? "" : "Select cost centers"} inputProps={{ ...params.inputProps, "aria-label": "Cost Center" }} />
        )}
        sx={inputSx}
      />
    ),
    reviewed_by: () => (
      <TextField
        size="small"
        fullWidth
        placeholder="Search by reviewer email"
        value={filters.reviewed_by.value}
        onChange={(e) => setValue("reviewed_by")(e.target.value)}
        inputProps={{ "aria-label": "Reviewed By" }}
        sx={inputSx}
      />
    ),
    email: () => (
      <TextField
        size="small"
        fullWidth
        placeholder="Search by email"
        value={filters.email.value}
        onChange={(e) => setValue("email")(e.target.value)}
        inputProps={{ "aria-label": "Email" }}
        sx={inputSx}
      />
    ),
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        startIcon={<FilterListIcon />}
        aria-haspopup="dialog"
        sx={{
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.85rem",
          height: 37,
          color: "#333",
          borderColor: count ? ACCENT : "#c4c4c4",
          backgroundColor: "#fff",
          "&:hover": { borderColor: ACCENT, backgroundColor: "#fff" },
        }}
      >
        Filters
        {count > 0 && (
          <Box
            component="span"
            aria-label={`${count} applied`}
            sx={{ ml: 1, minWidth: 20, height: 20, px: 0.6, borderRadius: "10px", backgroundColor: ACCENT, color: "#fff", fontSize: "0.72rem", lineHeight: "20px", textAlign: "center" }}
          >
            {count}
          </Box>
        )}
      </Button>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { width: 360, p: 2, mt: 0.5 }, role: "dialog", "aria-label": "Filters" }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
          <Typography sx={{ fontSize: "0.95rem", fontWeight: 700 }}>Filters</Typography>
          <Button size="small" disabled={count === 0} onClick={() => onFiltersChange(clearAllFilters(filters))} sx={{ textTransform: "none", fontSize: "0.8rem" }}>
            Clear all
          </Button>
        </Box>
        {FILTER_FIELDS.map((field) => {
          // Reviewed By only works with an Approved, Rejected or Failed status.
          const unavailable = field.key === "reviewed_by" && !canFilterByReviewer(filters);
          return (
            <Box key={field.key} sx={{ py: 0.25 }}>
              <FormControlLabel
                disabled={unavailable}
                sx={{ mx: 0, "& .MuiFormControlLabel-label": { fontSize: "0.88rem", fontWeight: 600 } }}
                control={<Checkbox size="small" checked={filters[field.key].on && !unavailable} onChange={() => onFiltersChange(toggleFilter(filters, field.key))} />}
                label={field.label}
              />
              {unavailable && (
                <Typography sx={{ ml: 3.5, mb: 0.5, fontSize: "0.72rem", color: "#999" }}>Choose Approved, Rejected or Failed status first.</Typography>
              )}
              {!unavailable && filters[field.key].on && <Box sx={{ ml: 3.5, mb: 1 }}>{controls[field.key]()}</Box>}
            </Box>
          );
        })}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
          <Button size="small" variant="contained" disableElevation onClick={() => setAnchorEl(null)} sx={{ textTransform: "none", backgroundColor: ACCENT, "&:hover": { backgroundColor: "#1f6e0a" } }}>
            Done
          </Button>
        </Box>
      </Popover>
    </>
  );
};

// What is applied, one removable chip each, plus Clear all. Renders nothing
// when no filter has a value, so the table sits right under the tabs.
export const UlbFilterChips = ({ filters, onFiltersChange }) => {
  const { costCenterNameById } = useCostCenters();
  const chips = getFilterChips(filters, { costCenterNameById });
  if (chips.length === 0) return null;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", mb: 1 }} aria-label="Applied filters">
      {chips.map(({ key, label, text }) => (
        <Chip
          key={key}
          size="small"
          label={
            <>
              <span style={{ color: "#777" }}>{label}: </span>
              <strong>{text}</strong>
            </>
          }
          onDelete={() => onFiltersChange(toggleFilter(filters, key))}
          sx={{ backgroundColor: "rgba(38,137,13,0.08)", border: "1px solid rgba(38,137,13,0.25)", fontSize: "0.78rem" }}
        />
      ))}
      {chips.length > 1 && (
        <Button size="small" onClick={() => onFiltersChange(clearAllFilters(filters))} sx={{ textTransform: "none", fontSize: "0.78rem", minWidth: 0 }}>
          Clear all
        </Button>
      )}
    </Box>
  );
};
